import { useMemo } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { toast } from 'react-toastify';
import { ChildForm } from '@ui';
import { useBoolean } from 'react-use';
import { FormActions } from '@features/forms/FormActions/FormActions';
import {
  CreateOpoContactForm,
  DEFAULT_CONTACT_VALUES,
  opoContactFormSchema,
  CreateOpoContactFormValues,
} from '@features/forms/CreateOpoContactForm';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { CreateOpoUserRequestBody, CreateOpoContactRequestBody, OpoOrganizationContact } from '@/api/apis/opo';
import { FileType } from '@/queries/file';
import { useCreateOpoContactMutation, useCreateOpoUserMutation } from '@/queries/opo';
import { useCreateOpobffContactMutation } from '@/queries/opobff';

type CreateOpoContactModalProps = {
  isOpen: boolean;
  opoId: string | null;
  onClose: () => void;
  onCreated: (contact: OpoOrganizationContact) => void;
  showInviteAsUser?: boolean;
  useOpobffContact?: boolean;
};

const buildCreatedContact = (
  id: string,
  body: CreateOpoContactRequestBody,
  opoId: string | null,
): OpoOrganizationContact => {
  const membership = body.memberships.find((m) => m.opoOrganizationId === opoId);

  return {
    id,
    userId: null,
    firstName: body.firstName ?? null,
    lastName: body.lastName ?? null,
    email: body.email ?? null,
    primaryPhone: body.primaryPhone as string,
    secondaryPhone: body.secondaryPhone ?? null,
    photoPath: body.photoPath ?? '',
    isActive: body.isActive ?? true,
    createdAt: new Date().toISOString(),
    modifiedAt: new Date().toISOString(),
    deletedAt: null,
    isPrimary: false,
    roles: membership?.roles ?? [],
  };
};

export const CreateOpoContactModal = ({
  isOpen,
  onClose,
  onCreated,
  opoId,
  showInviteAsUser = false,
  useOpobffContact = false,
}: CreateOpoContactModalProps) => {
  const [isSubmitting, setIsSubmitting] = useBoolean(false);

  const { mutateAsync: createOpoContact } = useCreateOpoContactMutation();
  const { mutateAsync: createOpobffContact } = useCreateOpobffContactMutation();
  const { mutateAsync: createOpoUser } = useCreateOpoUserMutation();
  const { uploadFiles } = useUploadFile(FileType.Opo);

  const defaultValues = useMemo<CreateOpoContactFormValues>(
    () => ({
      ...DEFAULT_CONTACT_VALUES,
      inviteAsUser: false,
      parentId: opoId || '',
      memberships: opoId
        ? [
            {
              opoOrganizationId: opoId,
              roles: [],
            },
          ]
        : [],
    }),
    [opoId],
  );

  const handleSubmit = async (data: CreateOpoContactFormValues) => {
    try {
      setIsSubmitting(true);

      const contactId = uuidV4();

      let uploadedFile;

      if (data.photo) {
        uploadedFile = await uploadFiles([data.photo]);
      }

      const requestBody: CreateOpoContactRequestBody = {
        id: contactId,
        firstName: data.firstName ?? null,
        lastName: data.lastName ?? null,
        primaryPhone: data.primaryPhone ?? null,
        photoPath: uploadedFile ? uploadedFile[0].fileName : (data.photoPath ?? null),
        email: data.email ?? null,
        secondaryPhone: data.secondaryPhone ?? null,
        memberships: data.memberships.map(({ opoOrganizationId, roles }) => ({
          opoOrganizationId,
          roles,
        })),
      };

      const shouldCreateOpoUser = showInviteAsUser && data.inviteAsUser && data.email;

      if (shouldCreateOpoUser) {
        await createOpoUser(requestBody as CreateOpoUserRequestBody);
      } else if (useOpobffContact) {
        await createOpobffContact(requestBody);
      } else {
        await createOpoContact(requestBody);
      }

      toast.success('OPO Contact created successfully.');
      onCreated(buildCreatedContact(contactId, requestBody, opoId));
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return false;

  return (
    <Modal
      classNames={{
        wrapper: 'flex-wrap',
        base: 'p-6 bg-base-80 m-2!',
        header: 'text-hl! font-secondary p-0 pb-4 border-b-1 border-base-30',
        body: 'p-0 my-6',
        footer: 'p-0 pt-6 border-t-1 border-base-30 justify-between',
      }}
      size="4xl"
      backdrop="opaque"
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
    >
      <ModalContent>
        {(handleClose) => (
          <ChildForm<CreateOpoContactFormValues>
            defaultValues={defaultValues}
            schema={opoContactFormSchema}
            onSubmit={handleSubmit}
          >
            <ModalHeader>New OPO Contact</ModalHeader>

            <ModalBody>
              <CreateOpoContactForm showInviteAsUser={showInviteAsUser} useOpobffContact={useOpobffContact} />
            </ModalBody>

            <ModalFooter>
              <FormActions
                actions={{
                  size: 'md',
                  discard: {
                    label: 'Discard',
                    variant: 'light',
                    handler: handleClose,
                  },
                  submit: {
                    label: 'Create OPO Contact',
                    isSubmitting,
                  },
                }}
              />
            </ModalFooter>
          </ChildForm>
        )}
      </ModalContent>
    </Modal>
  );
};
